import { useRef } from 'react'

import { HotmartRum } from '@hot-observability-js/react'

import { useDecision } from '@optimizely/react-sdk'

import BannerSection from '@space-shell/common/components/bannerSection'
import { CollectionsWrapper } from '@space-shell/common/components/collections'
import HtmlHead from '@space-shell/common/components/htmlHead'
import { IdleRenderer } from '@space-shell/common/components/idleRenderer'
import LegacyClubRedirectModal from '@space-shell/common/components/legacyClubRedirectModal'
import LoaderFull from '@space-shell/common/components/loaderFull'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import useFeatureToggle from '@space-shell/common/hooks/useFeatureToggle'
import useFreeSignUp from '@space-shell/common/hooks/useFreeSignUp'
import { useLegacyClubRedirect } from '@space-shell/common/hooks/useLegacyClubRedirect'
import { Toggles } from '@space-shell/common/services/toggle'
import { useAuthStore, useMembershipStore } from '@space-shell/core/store'

import Bundle from '@space-shell/modules/bundle'
import CommunitiesInHome from '@space-shell/modules/communities/communitiesInHome'
import MyProgress from '@space-shell/modules/home/components/myProgress'
import Showcase from '@space-shell/modules/home/components/showcase'
import Live from '@space-shell/modules/live'
import { MentorshipCarousel } from '@space-shell/modules/mentorship/components/mentorshipCarousel'
import { useGetSessions } from '@space-shell/modules/mentorship/hooks/useGetSessions'
import ProductDrawer from '@space-shell/modules/products/components/productDrawer'
import ProductProvider from '@space-shell/modules/products/context/provider'

import { useHome } from './hooks'

export const Home = () => {
  useHome()
  const { authStatus } = useAuthContext()
  const { userName, id } = useAuthStore()
  const { data: sessions } = useGetSessions()
  const { isLoadingFreeSignUp } = useFreeSignUp()
  const { membershipData } = useMembershipStore()
  const containerPurchaseChargeAlertRef = useRef(null)
  const [mentorshipFF] = useDecision('mcm_enable_mentorship')
  const { isRedirectedUser, redirectLoggedUser } = useLegacyClubRedirect()
  const { data: isLiveEnabled } = useFeatureToggle({ toggleName: Toggles.LiveKeepWatching })
  const { data: isEnableHotmartClubDynamicSections } = useFeatureToggle({
    toggleName: Toggles.EnableHotmartClubDynamicSections
  })

  const isUserAuthenticated = authStatus === AuthStatus.AUTHENTICATED

  const showMentorshipCarousel =
    isUserAuthenticated && mentorshipFF.enabled && sessions && sessions?.length > 0

  HotmartRum.setUser({
    username: userName,
    userId: id,
    membershipName: membershipData?.name,
    membershipOwnerCode: membershipData?.ownerUcode,
    membershipSlug: membershipData?.slug,
    orientation: membershipData?.settings?.orientation
  })

  if (isRedirectedUser && isUserAuthenticated) {
    redirectLoggedUser()
  }

  if (isLoadingFreeSignUp) {
    return <LoaderFull />
  }

  return (
    <div className='_mb-5 _pb-5 '>
      <HtmlHead title={membershipData?.name as string} />

      <BannerSection banners={membershipData?.settings?.banners} slideAuto />

      <section className='_mt-4'>
        {showMentorshipCarousel && <MentorshipCarousel sessions={sessions} />}

        {isUserAuthenticated && isLiveEnabled && <Live />}

        {isUserAuthenticated && !isEnableHotmartClubDynamicSections && <MyProgress />}
        <ProductProvider>
          <Showcase />
          <ProductDrawer />
        </ProductProvider>

        <Bundle />

        <IdleRenderer extraDelay={5000} minIdleTime={30} requiredIdleFrames={3}>
          <CommunitiesInHome />
        </IdleRenderer>

        {!isUserAuthenticated && <LegacyClubRedirectModal />}
      </section>

      <IdleRenderer extraDelay={3000} minIdleTime={10} requiredIdleFrames={3}>
        <section id='app-collections-root' ref={containerPurchaseChargeAlertRef}>
          <CollectionsWrapper
            container={containerPurchaseChargeAlertRef}
            appOrigin={'club_showcase'}
          />
        </section>
      </IdleRenderer>
    </div>
  )
}
